import { createAsyncThunk } from '@reduxjs/toolkit';

import sendRequest from '../../../service/sendRequest';
import consumerApi, { AUTH_SHAPE } from '../../../service/consumerApi';
import { enqueueSnackbar } from '../../notifier/notifierActions';
import { clearConsumerSession, setConsumerSession } from '../consumerReducer';
import { pickCredentials } from '../consumerSession';
import { PROJECT_TYPES, SONAR_PROJECT_RANGE_TYPE, SONAR_RANGE_TYPE } from '../../../constants';
import errorNotification from '../../../utils/errorNotification';
import { constructSonarRequestDates } from '../../sonar/sonarActions';
import { cancelNavig8, setNavig8AbortController } from '../../sonar/sonarReducer';

const GET_CONSUMER_SONAR_DATA = 'consumer/sonar/GET_CONSUMER_SONAR_DATA';
const CONSUMER_SONAR_LOGIN = 'consumer/sonar/CONSUMER_SONAR_LOGIN';
const GET_CONSUMER_SONAR_NAVIG8 = 'consumer/sonar/GET_CONSUMER_SONAR_NAVIG8';
const REPLY_CONSUMER_SONAR_NAVIG8 = 'consumer/sonar/REPLY_CONSUMER_SONAR_NAVIG8';

export const consumerSonarLogin = createAsyncThunk(
  CONSUMER_SONAR_LOGIN,
  async ({ projectName, projectPassword }, { dispatch, rejectWithValue }) => {
    const response = await sendRequest(
      () =>
        consumerApi.post('/api/consumer/sonar/login', null, {
          authShape: AUTH_SHAPE.FLAT,
          consumerCredentials: { projectName, projectPassword },
        }),
      {
        onError: (e) => {
          errorNotification(e, [
            { code: 403, errorText: 'wrongCredentials', errorSubText: 'pleaseTryAgain' },
            { code: 404, errorText: 'noDataCollectedYet', errorSubText: 'accessToTheProjectIsCurrentlyDenied' },
          ]);
          dispatch(clearConsumerSession());

          return rejectWithValue(e);
        },
      },
    );

    const { collectedFrom, collectedTill, rangeType } = response.data;

    if (!collectedFrom || !collectedTill) {
      dispatch(
        enqueueSnackbar({
          message: 'noDataCollectedYet',
          options: {
            variant: 'error',
            subMessage: 'accessToTheProjectIsCurrentlyDenied',
          },
        }),
      );

      return rejectWithValue(null);
    }

    dispatch(
      setConsumerSession({
        projectType: PROJECT_TYPES.SONAR,
        projectName,
        projectPassword,
      }),
    );
    return { collectedFrom, collectedTill, rangeType: rangeType || SONAR_PROJECT_RANGE_TYPE.DAILY_RANGE };
  },
);

export const getConsumerSonarData = createAsyncThunk(
  GET_CONSUMER_SONAR_DATA,
  async (_, { getState, rejectWithValue }) => {
    const {
      [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
      [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
    } = getState().sonarDashboard.dateRanges;
    const serverUrl = getState().securityNew.serverUrl;

    if (!pickCredentials(getState().consumer.session)) {
      return rejectWithValue(null);
    }

    const ranges = constructSonarRequestDates({
      comparableStartDate,
      comparableEndDate,
      compareToStartDate,
      compareToEndDate,
    });

    if (ranges) {
      const requestBody = { ranges };

      const response = await sendRequest(() =>
        consumerApi.post(`/api/consumer/sonar/calculations`, requestBody, {
          authShape: AUTH_SHAPE.LOGIN_REQUEST,
        }),
      );

      return { content: response.data.calculations, serverUrl };
    } else {
      return rejectWithValue(null);
    }
  },
);

export const getConsumerSonarNavig8 = createAsyncThunk(
  GET_CONSUMER_SONAR_NAVIG8,
  async (_, { rejectWithValue, getState, dispatch }) => {
    const {
      [SONAR_RANGE_TYPE.COMPARABLE]: { startDate: comparableStartDate, endDate: comparableEndDate },
      [SONAR_RANGE_TYPE.COMPARE_TO]: { startDate: compareToStartDate, endDate: compareToEndDate },
    } = getState().sonarDashboard.dateRanges;

    const ranges = constructSonarRequestDates({
      comparableStartDate,
      comparableEndDate,
      compareToStartDate,
      compareToEndDate,
    });

    const controller = new AbortController();
    dispatch(setNavig8AbortController(controller));

    if (ranges) {
      const requestBody = { ranges };

      const response = await sendRequest(
        () =>
          consumerApi.post(`/api/consumer/sonar/navig8`, requestBody, {
            authShape: AUTH_SHAPE.LOGIN_REQUEST,
            signal: controller.signal,
          }),
        {
          onError: (e) => {
            dispatch(cancelNavig8());
            return rejectWithValue(e);
          },
        },
      );

      dispatch(setNavig8AbortController(null));
      return response.data;
    } else {
      dispatch(cancelNavig8());
      return rejectWithValue(null);
    }
  },
);

export const replyConsumerSonarNavig8 = createAsyncThunk(
  REPLY_CONSUMER_SONAR_NAVIG8,
  async ({ reply }, { rejectWithValue, getState, dispatch }) => {
    const { thread } = getState().sonarDashboard.navig8;

    const controller = new AbortController();
    dispatch(setNavig8AbortController(controller));

    const requestBody = {
      reply,
      threadId: thread,
    };

    const response = await sendRequest(
      () =>
        consumerApi.post(`/api/consumer/sonar/navig8/reply`, requestBody, {
          authShape: AUTH_SHAPE.PROJECT_REQUEST,
          signal: controller.signal,
        }),
      {
        onError: (e) => {
          dispatch(cancelNavig8());
          return rejectWithValue(e);
        },
      },
    );

    dispatch(setNavig8AbortController(null));
    return response.data;
  },
);
